"use client";

import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../../contexts/AuthContext";
import { adminColegioAdministracionService } from "../../../services/admin-colegio/administracion.service";
import AdminColegioLayout from "../components/admincolegiolayout";

import type {
  AdminColegioAsignacion,
  AdminColegioCurso,
  AdminColegioProfesor,
} from "./dto/admin.dto";

import {
  BookOpen,
  CheckCircle2,
  ClipboardList,
  PlusCircle,
  Search,
  Users,
  XCircle,
} from "lucide-react";

export default function AdminColegioAdministrarPage() {
  const { user } = useAuth();

  const [profesores, setProfesores] = useState<AdminColegioProfesor[]>([]);
  const [cursos, setCursos] = useState<AdminColegioCurso[]>([]);
  const [asignaciones, setAsignaciones] = useState<AdminColegioAsignacion[]>(
    []
  );
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [loading, setLoading] = useState(true);

  const [searchNombre, setSearchNombre] = useState("");
  const [searchCurso, setSearchCurso] = useState("");

  const [showCrearAsignacionModal, setShowCrearAsignacionModal] =
    useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [showAsignacionModal, setShowAsignacionModal] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [asignacionEditando, setAsignacionEditando] =
    useState<AdminColegioAsignacion | null>(null);

  const [nuevaAsignacion, setNuevaAsignacion] = useState({
    idUsuario: "",
    idCurso: "",
  });

  const loadData = async () => {
    setLoading(true);

    const [profesoresRes, cursosRes, asignacionesRes] =
      await Promise.allSettled([
        adminColegioAdministracionService.getProfesores(),
        adminColegioAdministracionService.getCursos(),
        adminColegioAdministracionService.getAsignaciones(),
      ]);

    if (profesoresRes.status === "fulfilled")
      setProfesores(profesoresRes.value);
    if (cursosRes.status === "fulfilled") setCursos(cursosRes.value);
    if (asignacionesRes.status === "fulfilled")
      setAsignaciones(asignacionesRes.value);

    setLoading(false);
  };

  useEffect(() => {
    if (user) loadData();
  }, [user]);

  const profesoresFiltrados = useMemo(
    () =>
      profesores.filter((p) =>
        `${p.nombres} ${p.apellidos}`
          .toLowerCase()
          .includes(searchNombre.toLowerCase())
      ),
    [profesores, searchNombre]
  );

  const cursosFiltrados = useMemo(
    () =>
      cursos.filter((c) =>
        c.nombre.toLowerCase().includes(searchCurso.toLowerCase())
      ),
    [cursos, searchCurso]
  );

  return (
    <AdminColegioLayout>
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 py-8 space-y-6">
          <div className="bg-gradient-to-r from-purple-600 to-purple-800 rounded-2xl p-6 text-white shadow">
            <h1 className="text-3xl font-bold">Administración</h1>
            <p className="text-white/80">
              Gestión de profesores, cursos y asignaciones
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card
              icon={<Users />}
              title="Profesores"
              value={profesores.length}
            />
            <Card icon={<BookOpen />} title="Cursos" value={cursos.length} />
            <Card
              icon={<CheckCircle2 />}
              title="Asignaciones vigentes"
              value={asignaciones.filter((a) => a.vigente).length}
            />
            <Card
              icon={<XCircle />}
              title="No vigentes"
              value={asignaciones.filter((a) => !a.vigente).length}
            />
          </div>

          <div className="bg-white p-4 rounded-2xl shadow flex flex-wrap gap-3 items-center">
            <SearchInput
              placeholder="Buscar profesor"
              value={searchNombre}
              onChange={setSearchNombre}
            />
            <SearchInput
              placeholder="Buscar curso"
              value={searchCurso}
              onChange={setSearchCurso}
            />

            <div className="flex-1" />

            <button
              onClick={() => setShowCrearAsignacionModal(true)}
              className="bg-purple-600 hover:bg-purple-700 text-white px-5 py-2 rounded-xl font-semibold flex items-center gap-2"
            >
              <PlusCircle size={18} />
              Nueva asignación
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ListBox title="Profesores" icon={<Users />}>
              {profesoresFiltrados.map((p) => (
                <Row key={p.idUsuario}>
                  <div>
                    <p className="font-medium">
                      {p.nombres} {p.apellidos}
                    </p>
                    <p className="text-sm text-gray-500">{p.email}</p>
                  </div>
                </Row>
              ))}
            </ListBox>

            <ListBox title="Cursos" icon={<BookOpen />}>
              {cursosFiltrados.map((c) => (
                <Row key={c.idCurso}>{c.nombre}</Row>
              ))}
            </ListBox>
          </div>

          <div className="bg-white rounded-2xl shadow overflow-hidden">
            <div className="px-6 py-4 border-b font-semibold flex items-center gap-2">
              <ClipboardList size={18} />
              Asignaciones
            </div>

            <table className="w-full">
              <thead className="bg-gray-50 text-sm">
                <tr>
                  <th className="p-3 text-left">Profesor</th>
                  <th className="p-3 text-left">Curso</th>
                  <th className="p-3 text-left">Fecha</th>
                  <th className="p-3 text-left">Estado</th>
                  <th className="p-3 text-left">Acciones</th>
                </tr>
              </thead>

              <tbody>
                {asignaciones.map((a) => {
                  const profesor =
                    a.profesor ??
                    profesores.find((p) => p.idUsuario === a.profesorId);

                  const curso = cursos.find((c) => c.idCurso === a.cursoId);

                  return (
                    <tr
                      key={a.idAsignacion}
                      className="border-t hover:bg-gray-50"
                    >
                      <td className="p-3">
                        {profesor
                          ? `${profesor.nombres} ${profesor.apellidos}`
                          : "—"}
                      </td>
                      <td className="p-3">{curso?.nombre ?? "—"}</td>
                      <td className="p-3">
                        {a.fecha ? new Date(a.fecha).toLocaleDateString() : "—"}
                      </td>
                      <td className="p-3">
                        {a.vigente ? (
                          <Badge color="green">Vigente</Badge>
                        ) : (
                          <Badge color="red">No vigente</Badge>
                        )}
                      </td>
                      <td className="p-3">
                        <button
                          onClick={() => {
                            setAsignacionEditando(a);
                            setShowAsignacionModal(true);
                          }}
                          className="text-purple-600 font-semibold hover:underline"
                        >
                          Editar
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {showCrearAsignacionModal && (
          <Modal
            title="Nueva asignación"
            onClose={() => setShowCrearAsignacionModal(false)}
          >
            <select
              className="input"
              value={nuevaAsignacion.idUsuario}
              onChange={(e) =>
                setNuevaAsignacion({
                  ...nuevaAsignacion,
                  idUsuario: e.target.value,
                })
              }
            >
              <option value="">Profesor</option>
              {profesores.map((p) => (
                <option key={p.idUsuario} value={p.idUsuario}>
                  {p.nombres} {p.apellidos}
                </option>
              ))}
            </select>

            <select
              className="input"
              value={nuevaAsignacion.idCurso}
              onChange={(e) =>
                setNuevaAsignacion({
                  ...nuevaAsignacion,
                  idCurso: e.target.value,
                })
              }
            >
              <option value="">Curso</option>
              {cursos.map((c) => (
                <option key={c.idCurso} value={c.idCurso}>
                  {c.nombre}
                </option>
              ))}
            </select>

            <ModalActions
              onCancel={() => setShowCrearAsignacionModal(false)}
              onConfirm={async () => {
                const profesorId = Number(nuevaAsignacion.idUsuario);
                const cursoId = Number(nuevaAsignacion.idCurso);

                if (!profesorId || !cursoId) {
                  alert("Debes seleccionar un profesor y un curso");
                  return;
                }

                await adminColegioAdministracionService.createAsignacion({
                  profesorId,
                  cursoId,
                });

                setShowCrearAsignacionModal(false);
                setNuevaAsignacion({ idUsuario: "", idCurso: "" });
                loadData();
              }}
            />
          </Modal>
        )}
      </div>
    </AdminColegioLayout>
  );
}

const Card = ({ icon, title, value }: any) => (
  <div className="bg-white p-5 rounded-2xl shadow flex gap-4 items-center">
    <div className="w-12 h-12 rounded-xl bg-purple-50 flex items-center justify-center text-purple-700">
      {icon}
    </div>
    <div>
      <p className="text-sm text-gray-500">{title}</p>
      <p className="text-2xl font-bold">{value}</p>
    </div>
  </div>
);

const SearchInput = ({ placeholder, value, onChange }: any) => (
  <div className="flex items-center gap-2 border rounded-xl px-3 py-2 w-72">
    <Search size={18} className="text-gray-400" />
    <input
      className="outline-none w-full"
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  </div>
);

const ListBox = ({ title, icon, children }: any) => (
  <div className="bg-white rounded-2xl shadow">
    <div className="px-6 py-4 border-b font-semibold flex items-center gap-2">
      {icon}
      {title}
    </div>
    <div>{children}</div>
  </div>
);

const Row = ({ children }: any) => (
  <div className="px-6 py-3 border-t hover:bg-gray-50">{children}</div>
);

const Badge = ({ color, children }: any) => (
  <span
    className={`px-3 py-1 rounded-full text-sm font-semibold ${
      color === "green"
        ? "bg-green-50 text-green-700"
        : "bg-red-50 text-red-700"
    }`}
  >
    {children}
  </span>
);

const Modal = ({ title, onClose, children }: any) => (
  <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
    <div className="bg-white p-6 rounded-2xl w-96 space-y-4">
      <h2 className="text-xl font-semibold">{title}</h2>
      {children}
      <button
        onClick={onClose}
        className="text-sm text-gray-500 hover:underline"
      >
        Cancelar
      </button>
    </div>
  </div>
);

const ModalActions = ({ onCancel, onConfirm, disabled }: any) => (
  <div className="flex justify-end gap-2">
    <button onClick={onCancel} className="border px-4 py-2 rounded-xl">
      Cancelar
    </button>

    <button
      onClick={onConfirm}
      disabled={disabled}
      className={`px-4 py-2 rounded-xl text-white ${
        disabled
          ? "bg-purple-300 cursor-not-allowed"
          : "bg-purple-600 hover:bg-purple-700"
      }`}
    >
      Guardar
    </button>
  </div>
);
